import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AffiliatePartner {
  id: string;
  partner_name: string;
  category: string;
  affiliate_url: string;
  description: string;
  logo_url: string | null;
  active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  personal_endorsement: string | null;
  star_rating: number | null;
  pros: string[] | null;
  cons: string[] | null;
  button_text: string | null;
  featured: boolean;
}

export const useAffiliatePartners = () => {
  return useQuery({
    queryKey: ['affiliate-partners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('affiliate_partners')
        .select('*')
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as AffiliatePartner[];
    },
  });
};

export const useAffiliatePartnersByTags = (tagIds: string[]) => {
  return useQuery({
    queryKey: ['affiliate-partners-by-tags', tagIds],
    queryFn: async () => {
      if (!tagIds || tagIds.length === 0) {
        // Fallback: return top 3 partners by display order
        const { data, error } = await supabase
          .from('affiliate_partners')
          .select('*')
          .eq('active', true)
          .order('display_order', { ascending: true })
          .limit(3);

        if (error) throw error;
        return data as AffiliatePartner[];
      }

      // Get partners that match the tags
      const { data: categoryData, error: categoryError } = await supabase
        .from('affiliate_partner_categories')
        .select('affiliate_partner_id')
        .in('tag_id', tagIds);

      if (categoryError) throw categoryError;

      if (!categoryData || categoryData.length === 0) {
        // No matching partners, return top 3
        const { data, error } = await supabase
          .from('affiliate_partners')
          .select('*')
          .eq('active', true)
          .order('display_order', { ascending: true })
          .limit(3);

        if (error) throw error;
        return data as AffiliatePartner[];
      }

      const partnerIds = categoryData.map((c) => c.affiliate_partner_id);

      const { data, error } = await supabase
        .from('affiliate_partners')
        .select('*')
        .in('id', partnerIds)
        .eq('active', true)
        .order('display_order', { ascending: true })
        .limit(3);

      if (error) throw error;
      return data as AffiliatePartner[];
    },
    enabled: tagIds !== undefined,
  });
};

export const useAffiliatePartnersByCategory = (category: string) => {
  return useQuery({
    queryKey: ['affiliate-partners-by-category', category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('affiliate_partners')
        .select('*')
        .eq('category', category)
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as AffiliatePartner[];
    },
    enabled: !!category,
  });
};
